import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { Input, InputNumber, Modal, TableColumnsType } from "antd";
import { AnyObject } from "antd/lib/_util/type";
import { ColumnsType } from "antd/lib/table";
import _ from "lodash";

import { productList, ProductListBody, ProductListObjParams, ProductListParams } from "@/apis/product";
import useDataList from "@/hooks/useDataList";

import { CommonListParams } from "@/types/common";
import { Product } from "@/types/product";

import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";

export interface ProductWithNum extends Product {
  quantity?: number;
}

interface ProductSelectProps {
  values?: Array<ProductWithNum>;
  handleOk: (products: Array<ProductWithNum>) => void;
  openProduct: boolean;
  setOpenProduct: Dispatch<SetStateAction<boolean>>;
  setDisplay?: Dispatch<SetStateAction<string>>;
  isQuantity?: boolean;
}

const ProductSelectMulti: FC<ProductSelectProps> = ({ handleOk, values, openProduct, setOpenProduct, setDisplay, isQuantity = false }) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [, total, listLoading, { fetchList }] = useDataList<Product, [ProductListObjParams]>({ fetchApi: productList });
  const [products, setProducts] = useState<Array<ProductWithNum>>([]);
  const [selectProIds, setSelectProIds] = useState<Array<string>>([]);
  const [selectPros, setSelectPros] = useState<Array<ProductWithNum>>([]);
  const [selectProsTemp, setSelectProsTemp] = useState<Array<ProductWithNum>>([]);
  const [selectProIdsTemp, setSelectProIdsTemp] = useState<Array<string>>([]);

  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    if (openProduct) {
      fetchProducts(0, "");
    }
  }, [openProduct]);

  const fetchProducts = (offset: number, keyword: string) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    let params: CommonListParams<ProductListBody> = {
      body: { keyword },
      pagination: { offset, take },
    }
    if (!!params?.body?.keyword) {
      let kw = params.body.keyword.trim();
      let strs: string[] = []
      if (kw.includes(' ')) {
        strs = kw.split(' ')
        params.body = undefined
        params.strs = strs
      }
    }
    fetchList({
      params,
    }).then(({ body }) => {
      setProducts(body ?? []);
    });
  };

  const columns: TableColumnsType<ProductWithNum> = [
    {
      title: t("product.code"),
      dataIndex: "productCode",
      render: (_, product) => (
        <SText style={{ width: 100 }} text={product?.code} />
      ),
    },
    {
      title: t("product.name"),
      dataIndex: "productName",
      render: (_, product) => (
        <SText style={{ width: 160 }} text={product?.name} />
      ),
    },
    {
      title: t("product.brand1"),
      dataIndex: "productModel1",
      render: (_, product) => (
        <SText style={{ width: 500 }} text={product?.model} />
      ),
    },
    {
      title: t("product.brand2"),
      dataIndex: "productModel2",
      render: (_, product) => (
        <SText style={{ width: 500 }} text={product?.t1 ?? ''} />
      ),
    },
    {
      title: t("product.uom"),
      dataIndex: "productUom",
      render: (_, product) => <SText text={product?.uom} />,
    },
    {
      title: <SText text={t("product.preReady")} />,
      width: 120,
      render(value, row, rowIndex) {
        return (row.preReady || 0) === 1 ? <SText text={t('product.isTrue')} /> : <SText text={t('product.isFalse')} />
      },
    },
    {
      title: t("order.quantity"),
      dataIndex: "quantity",
      fixed: "right",
      width: 120,
      hidden: !isQuantity,
      render: (_, product) => <InputNumber changeOnWheel
        disabled={!selectProIds.includes(product.id)}
        min={0}
        onChange={val => setSelectPros(pros => pros.map(pro => pro.id === product.id ? { ...pro, quantity: Number(val) } : pro))}
      />
    }
  ];

  return (
    <Modal
      centered
      open={openProduct}
      width={1000}
      onCancel={() => {
        setOpenProduct(false);
        setSelectProIds([]);
        setSelectPros([]);
        setSelectProsTemp([])
        setSelectProIdsTemp([])
        setDisplay && setDisplay('none')
        setProducts([])
      }}
      title={t("requirement.selectPro")}
      onOk={() => {
        handleOk([...selectPros, ...selectProsTemp]);
        setOpenProduct(false);
        setSelectProIds([]);
        setSelectPros([]);
        setSelectProsTemp([])
        setSelectProIdsTemp([])
        setDisplay && setDisplay('none')
        setProducts([])
      }}
    >
      <div style={{ height: "600px" }}>
        <STableLayout
          loading={listLoading}
          dataSource={products}
          rowKey="id"
          rowSelection={{
            type: "checkbox",
            fixed: true,
            selectedRowKeys: selectProIds,
            onChange: (val, n) => {
              flushSync(() => {
                setSelectProIds(() =>
                  Array.from(new Set(val as Array<string>))
                );
                setSelectPros(() => _.uniqBy(n as Array<Product>, "id"));
              });
            },
            getCheckboxProps: (record) => ({
              name: record.id,
              // disabled: !!values.find((item) => item?.id === record.id),
            }),
          }}
          columns={columns as ColumnsType<AnyObject>}
          scrollX={1400}
          scroll={{ y: 480 }}
          headerChildren={
            <Input.Search
              enterButton
              placeholder={t("requirement.searchPro")}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={(e) => {
                if (selectPros.length > 0) {
                  setSelectProsTemp([...selectProsTemp, ...selectPros])
                  setSelectProIdsTemp([...selectProIdsTemp, ...selectProIds])
                }
                fetchProducts(0, e);
                tableRef?.current?.resetTablePage();
              }}
            />
          }
          tableLayoutRef={tableRef}
          total={total}
          handleChange={(offset) => {
            fetchProducts(offset, keyword);
          }}
        />
      </div>
    </Modal>
  );
};

export default ProductSelectMulti;
